// gameengine/gameResearch.js

/**
 * Processes research progress
 * @param {Object} currentResearch - Active research project
 * @param {Array} researchList - List of all research projects
 * @returns {Object} Updated research state
 */
export const processResearch = (currentResearch, researchList) => {
  if (!currentResearch || currentResearch.progress >= 100) {
    return { currentResearch, researchList, completed: null };
  }

  const progressPerDay = 100 / (currentResearch.duration || 1);
  const newProgress = Math.min(currentResearch.progress + progressPerDay, 100);
  
  const updatedResearch = {
    ...currentResearch,
    progress: newProgress,
    isDirty: true,
    ...(newProgress >= 100 && {
      completed: 1,
      completed_at: new Date().toISOString()
    })
  };

  const completed = newProgress >= 100 ? updatedResearch : null;

  return {
    currentResearch: newProgress >= 100 ? null : updatedResearch,
    researchList: researchList.map(r => 
      r.id === currentResearch.id ? updatedResearch : r
    ),
    completed
  };
};

/**
 * Checks if research will complete on the next day
 * @param {Object} research - Research project to check
 * @returns {boolean} Whether research completes next day
 */
export const willResearchCompleteNextDay = (research) => {
  if (!research) return false;
  const progressPerDay = 100 / (research.duration || 1);
  return research.progress + progressPerDay >= 100;
};

/**
 * Calculates daily research progress
 * @param {Object} research - Research project
 * @returns {number} Progress per day
 */
export const getProgressPerDay = (research) => {
  if (!research || !research.duration) return 0;
  return 100 / research.duration;
};

/**
 * Gets days remaining for research
 * @param {Object} research - Research project
 * @returns {number} Days remaining
 */
export const getDaysRemaining = (research) => {
  if (!research) return 0;
  const progressPerDay = getProgressPerDay(research);
  const remainingProgress = 100 - research.progress;
  return Math.ceil(remainingProgress / progressPerDay);
};

/**
 * Checks if research is completed
 * @param {Object} research - Research project
 * @returns {boolean} Whether research is completed
 */
export const isResearchCompleted = (research) => {
  return research && research.progress >= 100;
};